import React, { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'nt_theme';
const THEMES = ['light', 'dark', 'chocolate', 'ink', 'taupe'];
const THEME_COLORS = {
  light: '#FFE8D6',
  dark: '#14111c',
  chocolate: '#FDFBD4',
  ink: '#FFFFE3',
  taupe: '#FDFBD4',
};

const ThemeContext = createContext(null);

function readStoredTheme() {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(value) ? value : 'light';
  } catch {
    return 'light';
  }
}

function applyTheme(theme) {
  if (theme === 'light') {
    delete document.documentElement.dataset.theme;
  } else {
    document.documentElement.dataset.theme = theme;
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', THEME_COLORS[theme] || THEME_COLORS.light);
  }
}

export const ThemeProvider = ({ children }) => {
  // Saved HTML is always the default scheme. Read storage after mount so
  // Night (and the other palettes) do not mismatch on hydrate.
  const [theme, setThemeState] = useState('light');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setThemeState(readStoredTheme());
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme, ready]);

  const setTheme = (next) => {
    if (!THEMES.includes(next) || next === theme) return;
    setThemeState(next);
    fetch('/api/v1/visitors/night-mode', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: window.location.pathname, theme: next }),
      keepalive: true,
    }).catch(() => {});
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
